import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { uploadAttachment } from "@/lib/erp/docs";
import { useAuthStore } from "@/lib/store/authStore";

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export interface Collection {
  name: string;
  tenant: string;
  title: string;
  slug: string;
  description: string;
  banner_image: string | null;
  fallback_image: string | null;
  parent_collection: string | null;
  sort_order: string;
  is_visible: boolean;
  display_order: number;
  product_count: number;
}

export interface CollectionProduct {
  item_code: string;
  item_name: string;
  image: string | null;
  item_group: string | null;
  disabled: boolean;
  storefront_enabled: boolean;
}

export interface StorefrontItem {
  name: string;
  item_name: string;
  image: string | null;
  item_group: string | null;
  disabled: number;
  storefront_enabled: number;
}

// ────────────────────────────────────────────────────────────
// Query Keys
// ────────────────────────────────────────────────────────────

export interface ItemCollectionEntry {
  name: string;
  title: string;
  slug: string;
  is_visible: boolean;
}

export const collectionQueryKeys = {
  tenantKey: () => ["storefront-tenant-key"] as const,
  list: (tenant: string) => ["storefront-collections", tenant] as const,
  products: (name: string) =>
    ["storefront-collection-products", name] as const,
  assignable: (search: string) =>
    ["storefront-assignable-products", search] as const,
  itemCollections: (itemCode: string) =>
    ["storefront-item-collections", itemCode] as const,
};

// ────────────────────────────────────────────────────────────
// Tenant Resolution
// ────────────────────────────────────────────────────────────

/**
 * Resolve the current user's Storefront Tenant name.
 * Mode-agnostic: uses getERPClient().list() which applies tenant isolation
 * in both direct mode (company filter) and control_plane mode (JWT scope).
 */
export function useStorefrontTenantKey() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery({
    queryKey: collectionQueryKeys.tenantKey(),
    queryFn: async () => {
      const client = getERPClient();
      const tenants = await client.list<{ name: string }>({
        doctype: "Storefront Tenant",
        fields: ["name"],
        limit_page_length: 1,
      });
      return tenants.length > 0 ? tenants[0].name : null;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes — tenant key rarely changes
  });

  return {
    tenantKey: query.data ?? null,
    isLoading: query.isLoading,
  };
}

// ────────────────────────────────────────────────────────────
// Collection CRUD Hooks
// ────────────────────────────────────────────────────────────

const API_BASE = "zaviago_backend.storefront.api_collections";

export function useCollections(tenantKey: string | null) {
  return useQuery({
    queryKey: collectionQueryKeys.list(tenantKey ?? ""),
    queryFn: async () => {
      const client = getERPClient();
      const result = await client.call<{
        collections: Collection[];
        meta: Record<string, string>;
      }>(`${API_BASE}.get_collections`, { tenant: tenantKey });
      return result.collections;
    },
    enabled: !!tenantKey,
    placeholderData: (prev) => prev,
  });
}

export function useCreateCollection(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      slug?: string;
      description?: string;
      banner_image?: string;
      parent_collection?: string;
      sort_order?: string;
      is_visible?: number;
    }) => {
      const client = getERPClient();
      const result = await client.call<{
        collection: Collection;
        meta: Record<string, string>;
      }>(`${API_BASE}.create_collection`, { tenant: tenantKey, ...data });
      return result.collection;
    },
    onSuccess: () => {
      if (tenantKey) {
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.list(tenantKey),
        });
      }
    },
  });
}

export function useUpdateCollection(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      collection_name: string;
      title?: string;
      description?: string;
      banner_image?: string;
      parent_collection?: string;
      sort_order?: string;
      is_visible?: number;
      display_order?: number;
    }) => {
      const client = getERPClient();
      const result = await client.call<{
        collection: Collection;
        meta: Record<string, string>;
      }>(`${API_BASE}.update_collection`, { tenant: tenantKey, ...data });
      return result.collection;
    },
    onSuccess: () => {
      if (tenantKey) {
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.list(tenantKey),
        });
      }
    },
  });
}

export function useDeleteCollection(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (collectionName: string) => {
      const client = getERPClient();
      await client.call<{ success: boolean }>(`${API_BASE}.delete_collection`, {
        tenant: tenantKey,
        collection_name: collectionName,
      });
    },
    onSuccess: () => {
      if (tenantKey) {
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.list(tenantKey),
        });
      }
    },
  });
}

// ────────────────────────────────────────────────────────────
// Product Assignment Hooks
// ────────────────────────────────────────────────────────────

export function useCollectionProducts(
  tenantKey: string | null,
  collectionName: string | null
) {
  return useQuery({
    queryKey: collectionQueryKeys.products(collectionName ?? ""),
    queryFn: async () => {
      const client = getERPClient();
      const result = await client.call<{
        items: CollectionProduct[];
        meta: Record<string, string>;
      }>(`${API_BASE}.get_collection_products`, {
        tenant: tenantKey,
        collection_name: collectionName,
      });
      return result.items;
    },
    enabled: !!tenantKey && !!collectionName,
    placeholderData: (prev) => prev,
  });
}

export function useAssignProducts(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      collection_name: string;
      item_codes: string[];
    }) => {
      const client = getERPClient();
      return client.call<{
        assigned: string[];
        already_assigned: string[];
        meta: Record<string, string>;
      }>(`${API_BASE}.assign_products`, {
        tenant: tenantKey,
        collection_name: data.collection_name,
        item_codes: JSON.stringify(data.item_codes),
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.products(variables.collection_name),
      });
      // Only invalidate full list when called from Collection detail page (bulk ops).
      // Item detail toggle calls invalidate itemCollections separately — skip the
      // heavy list refetch (N+1 product counts) to keep toggles snappy.
    },
  });
}

export function useRemoveProducts(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      collection_name: string;
      item_codes: string[];
    }) => {
      const client = getERPClient();
      return client.call<{
        removed: string[];
        meta: Record<string, string>;
      }>(`${API_BASE}.remove_products`, {
        tenant: tenantKey,
        collection_name: data.collection_name,
        item_codes: JSON.stringify(data.item_codes),
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.products(variables.collection_name),
      });
    },
  });
}

/**
 * List all storefront-enabled products for the current tenant.
 * Used in the Products tab for the assignment checkbox list.
 * Server-side filtering by item_name when search query is provided.
 */
export function useStorefrontProducts(search: string, enabled: boolean) {
  return useQuery({
    queryKey: collectionQueryKeys.assignable(search),
    queryFn: async () => {
      const client = getERPClient();
      const filters: Record<string, unknown> = { storefront_enabled: 1 };
      if (search.trim()) {
        filters["item_name"] = ["like", `%${search.trim()}%`];
      }
      return client.list<StorefrontItem>({
        doctype: "Item",
        fields: [
          "name",
          "item_name",
          "image",
          "item_group",
          "disabled",
          "storefront_enabled",
        ],
        filters,
        limit_page_length: 0,
        order_by: "item_name asc",
      });
    },
    enabled,
    placeholderData: (prev) => prev,
  });
}

// ────────────────────────────────────────────────────────────
// Move / Reorder Hook (DnD tree)
// ────────────────────────────────────────────────────────────

export function useMoveCollection(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      collection_name: string;
      new_parent: string | null;
      new_index: number;
    }) => {
      const client = getERPClient();
      return client.call<{ ok: boolean }>(`${API_BASE}.move_collection`, {
        tenant: tenantKey,
        collection_name: data.collection_name,
        new_parent: data.new_parent ?? "",
        new_index: data.new_index,
      });
    },
    onSuccess: () => {
      if (tenantKey) {
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.list(tenantKey),
        });
      }
    },
  });
}

// ────────────────────────────────────────────────────────────
// Banner Image Upload
// ────────────────────────────────────────────────────────────

/**
 * Upload a banner image for a Storefront Collection.
 * Returns the file URL to pass to create/update mutations.
 */
export function useUploadCollectionBanner(tenantKey: string | null) {
  const queryClient = useQueryClient();

  return useMutation<
    { file_url: string; file_name: string; name: string },
    Error,
    { docName: string; file: File }
  >({
    mutationFn: async ({ docName, file }) => {
      return uploadAttachment({
        doctype: "Storefront Collection",
        name: docName,
        file,
      });
    },
    onSuccess: () => {
      if (tenantKey) {
        queryClient.invalidateQueries({
          queryKey: collectionQueryKeys.list(tenantKey),
        });
      }
    },
  });
}

// ────────────────────────────────────────────────────────────
// Item → Collections lookup (for item detail page)
// ────────────────────────────────────────────────────────────

/**
 * Get all collections that contain a specific item.
 * Used on the item detail page to show/manage collection membership.
 */
export function useItemCollections(
  tenantKey: string | null,
  itemCode: string | null
) {
  return useQuery({
    queryKey: collectionQueryKeys.itemCollections(itemCode ?? ""),
    queryFn: async () => {
      const client = getERPClient();
      const result = await client.call<{
        collections: ItemCollectionEntry[];
        meta: Record<string, string>;
      }>(`${API_BASE}.get_item_collections`, {
        tenant: tenantKey,
        item_code: itemCode,
      });
      return result.collections;
    },
    enabled: !!tenantKey && !!itemCode,
    placeholderData: (prev) => prev,
  });
}
